// 레이드 AI 용병 3명(RAID-PRD 4절): 카엘 리그 GLB를 복제해 역할 색으로 칠하고 무기 소품(방패·활·지팡이)을 손에 붙인다.
// 직업 영웅 모델(heroModels.ts)이 있는 역할은 그 모델로 바꾼다(CHARACTER-PRD §2: 방패병 → 브란, 사제 → 루멘). 무기는 GLB 안 손 메시,
// 색은 원래 텍스처 그대로. 읽기에 실패하면 예전 카엘 복제로 둔다.
// 영웅 모델(KaelModel)은 GLB 장면을 바꿔 쓰므로 여기서는 같은 파일을 따로 읽어(브라우저 캐시) 복제한다. 레이드에 처음 들어갈 때 읽는다.
// 동작은 게임 MercAct(서기·걷기·공격·시전·쓰러짐)를 Mixamo 클립에 대응하고, 클립의 골반 수평 이동은 지운다(몸 위치는 게임이 정한다).
// 머리 위: 이름표(스프라이트)와 HP 막대(스프라이트 2장, 할당 없음), 발밑: 역할 색 고리. 피격은 짧은 흰 발광, 쓰러지면 회색으로 눕는다.
import * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { MERCS, type MercDef } from '../../shared/content/raid';
import type { GameView, MercView } from '../../shared/types';
import type { GameClocks } from '../clock';
import { colored, merge } from '../geo';
import { contactMaterial } from '../env/fx';
import { labelSprite } from '../env/label';
import { KAEL_URL, KAEL_SCALE } from '../kael';
import { toonGradient } from './toon';
import { attachWeapon, loadWeaponGeometry, type WeaponKind } from '../heroWeapons';
import { clipName, heroModelShipped, instantiateHero, loadHeroModel, stripRootMotion, type HeroModelAsset, type HeroModelId } from '../heroModels';
import { angleDelta, damp } from '../util';

/** MercAct(game/raid)와 같은 번호 */
const ACT = { idle: 0, move: 1, attack: 2, cast: 3, down: 4 } as const;
/** 역할별 공격·시전 클립 */
const CLIPS: Record<MercDef['role'], { attack: string; cast: string }> = {
  tank: { attack: 'atk1', cast: 'powerup' },
  archer: { attack: 'atk3', cast: 'powerup' },
  priest: { attack: 'atk2', cast: 'powerup' },
};
/** 직업 영웅 모델로 그리는 역할과 그 모델의 공격·시전 클립(heroModels 동작 이름) */
const HERO_ROLE: Partial<Record<MercDef['role'], { id: HeroModelId; attack: 'atk1' | 'atk2' | 'atk3'; cast: 'skill1' | 'skill2' | 'skill3' }>> = {
  tank: { id: 'brann', attack: 'atk1', cast: 'skill3' },
  priest: { id: 'lumen', attack: 'atk1', cast: 'skill1' },
};
/**
 * 역할별 손 무기(Blender 생성 GLB, heroWeapons.ts): 방패병 = 수호자 룬 해머 + 방패, 궁수 = 장궁(왼손), 사제 = 성장.
 * 단계는 레이드 1단계 권장 레벨(지역2 = T1). 읽기에 실패하면 절차 소품(prop/offProp)
 */
const ROLE_WEAPONS: Record<MercDef['role'], readonly WeaponKind[]> = { tank: ['hammer', 'shield'], archer: ['bow'], priest: ['holy'] };
const ROLE_RING: Record<MercDef['role'], number> = { tank: 0x6aa8ff, archer: 0x7fe070, priest: 0xffe08a };
const BAR_W = 1.1, BAR_H = 0.13, HEAD_Y = 2.75;

interface Unit {
  def: MercDef;
  root: THREE.Group;
  model: THREE.Object3D;
  mixer: THREE.AnimationMixer;
  actions: Map<string, THREE.AnimationAction>;
  mat: THREE.MeshToonMaterial;
  ring: THREE.Mesh;
  label: THREE.Sprite;
  barBg: THREE.Sprite;
  barFill: THREE.Sprite;
  cur: string;
  /** 공격·시전 클립 이름 */
  attack: string;
  cast: string;
  /** 텍스처에 역할 색을 곱하는지(직업 영웅 모델은 원래 색) */
  tinted: boolean;
  act: number;
  actTick: number;
  yaw: number;
}

/** 손 본 아래 소품(본 배율 상쇄 뒤 m 단위). 손가락 +Y */
export function prop(role: MercDef['role']): THREE.BufferGeometry {
  if (role === 'tank') {
    // 한손검(오른손)
    return merge([
      colored(new THREE.BoxGeometry(0.07, 0.95, 0.025), 0xd8dde6, 0, 0.62, 0),
      colored(new THREE.BoxGeometry(0.26, 0.05, 0.06), 0xc9a24a, 0, 0.13, 0),
      colored(new THREE.CylinderGeometry(0.025, 0.025, 0.2, 6), 0x3a2a20, 0, 0.02, 0),
    ]);
  }
  if (role === 'archer') {
    // 활(오른손에 쥐고 위아래로)
    const parts: THREE.BufferGeometry[] = [];
    for (let i = 0; i < 6; i++) {
      const a0 = -0.9 + i * 0.36, len = 0.3;
      const g = new THREE.BoxGeometry(0.035, len, 0.035);
      g.rotateZ(a0 * 0.5); g.translate(Math.cos(a0) * 0.12 - 0.12, Math.sin(a0) * 0.6, 0);
      parts.push(colored(g, 0x6a4526));
    }
    parts.push(colored(new THREE.BoxGeometry(0.008, 1.12, 0.008), 0xf0ead8, 0.05, 0, 0));
    return merge(parts);
  }
  // 사제 지팡이 + 빛 구슬
  return merge([
    colored(new THREE.CylinderGeometry(0.03, 0.035, 1.6, 6), 0x8a6a3a, 0, 0.35, 0),
    colored(new THREE.OctahedronGeometry(0.12, 0), 0xfff2b0, 0, 1.2, 0),
  ]);
}

/** 왼손 소품(방패병 방패). 없으면 null */
export function offProp(role: MercDef['role']): THREE.BufferGeometry | null {
  if (role !== 'tank') return null;
  const g = new THREE.CylinderGeometry(0.42, 0.42, 0.06, 8);
  g.rotateZ(Math.PI / 2);
  return merge([colored(g, 0x355f9a, 0.06, 0.05, 0), colored(new THREE.CylinderGeometry(0.1, 0.1, 0.09, 8).rotateZ(Math.PI / 2), 0xc9a24a, 0.1, 0.05, 0)]);
}

/**
 * 용병 카엘(kael_t1.glb) 클립별 골반 높이 보정(m, +가 위) — 2026-10-30 foot_audit.py 계측(qa/l1030-look2/kael-foot-audit.md).
 * 예전 높이 고정(lockHeight)은 골반 상하 움직임을 지워 발이 가라앉았다(leap −14.1·run −7.9·atk4 −8.5cm). 높이를 살리고 이 값으로 발바닥 최저를 +0.4cm 근처에 둔다.
 * 주인공 카엘(kael.ts)은 같은 GLB를 쓰되 대기·달리기 런타임 보정을 따로 하므로 GLB 자체는 고치지 않는다.
 */
export const KAEL_CLIP_LIFT: Readonly<Record<string, number>> = {
  atk1: 0.0037, atk2: -0.0098, atk3: 0.0047, atk4: 0.0237, combo: 0.0052, dash: 0.0334, death: -0.0136,
  dodge: 0.0063, hurt: 0.0014, idle: -0.0131, leap: 0.022, powerup: 0.0014, run: 0.03, victory: -0.0031,
};

export class MercsVisual {
  readonly group = new THREE.Group();
  private loading: Promise<void> | null = null;
  private readonly units: Unit[] = [];
  private readonly owned: { dispose(): void }[] = [];
  /** 읽기 실패(레이드는 용병 없이도 진행된다: HUD 파티 창이 상태를 보여 준다) */
  failed = false;

  constructor() { this.group.name = 'raid-mercs'; this.group.visible = false; }

  get ready(): boolean { return this.units.length === MERCS.length; }

  load(): Promise<void> {
    if (!this.loading) this.loading = this.build().catch((err: unknown) => { this.failed = true; console.warn('용병 모델을 읽지 못함', err); });
    return this.loading;
  }

  private async build(): Promise<void> {
    const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    // 직업 영웅 모델은 카엘과 함께 읽기 시작한다(실패하면 그 역할만 카엘 복제)
    const heroLoads = new Map<HeroModelId, Promise<HeroModelAsset | null>>();
    for (const def of MERCS) {
      const h = HERO_ROLE[def.role];
      if (h && heroModelShipped(h.id) && !heroLoads.has(h.id)) heroLoads.set(h.id, loadHeroModel(h.id).catch((err: unknown) => { console.warn('영웅 모델을 읽지 못함', h.id, err); return null; }));
    }
    const weaponLoads = new Map<WeaponKind, Promise<THREE.BufferGeometry | null>>();
    for (const def of MERCS) for (const k of ROLE_WEAPONS[def.role]) {
      if (!weaponLoads.has(k)) weaponLoads.set(k, loadWeaponGeometry(k, 1).catch((err: unknown) => { console.warn('무기 모델을 읽지 못함', k, err); return null; }));
    }
    const gltf = await loader.loadAsync(KAEL_URL);
    const weaponMat = new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: null });
    this.owned.push(weaponMat);
    // 골반 수평 이동 제거(제자리 클립): 위치는 게임이 정한다
    const clips = new Map<string, THREE.AnimationClip>();
    for (const c of gltf.animations) clips.set(c.name, stripRootMotion(c, false, KAEL_CLIP_LIFT[c.name] ?? 0));
    const gradient = toonGradient();
    const blobGeo = new THREE.PlaneGeometry(1.4, 1.4); blobGeo.rotateX(-Math.PI / 2);
    const blobMat = contactMaterial(0.55);
    const ringGeo = new THREE.RingGeometry(0.62, 0.78, 40); ringGeo.rotateX(-Math.PI / 2);
    const barTex = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1); barTex.needsUpdate = true;
    this.owned.push(gradient, blobGeo, blobMat, ringGeo, barTex);
    weaponMat.gradientMap = gradient;
    for (const def of MERCS) {
      const heroRole = HERO_ROLE[def.role];
      const heroLoad = heroRole ? heroLoads.get(heroRole.id) : undefined;
      const hero = heroLoad ? await heroLoad : null;
      let model: THREE.Object3D;
      let mixer: THREE.AnimationMixer;
      let actions: Map<string, THREE.AnimationAction>;
      let skinned: THREE.SkinnedMesh | null = null;
      let weaponParts: ReadonlySet<THREE.Object3D> = new Set();
      if (hero && heroRole) {
        const inst = instantiateHero(hero);
        model = inst.root; mixer = inst.mixer; actions = inst.actions as Map<string, THREE.AnimationAction>;
        skinned = inst.skinned; weaponParts = new Set(inst.weaponMeshes);
      } else {
        model = cloneSkinned(gltf.scene);
        model.traverse((o) => { const m = o as THREE.SkinnedMesh; if (m.isSkinnedMesh && !skinned) skinned = m; });
        mixer = new THREE.AnimationMixer(model);
        actions = new Map<string, THREE.AnimationAction>();
        for (const [name, clip] of clips) actions.set(name, mixer.clipAction(clip));
      }
      const src = { map: null as THREE.Texture | null };
      if (skinned) { const sm = (skinned as THREE.SkinnedMesh).material as THREE.MeshStandardMaterial; if (sm && sm.map) src.map = sm.map; }
      // 역할 색: 카엘 복제는 텍스처에 색을 곱한다(같은 카엘이 다른 사람으로 보이게). 직업 영웅 모델은 원래 색
      const tinted = !hero;
      const mat = new THREE.MeshToonMaterial({ map: src.map, gradientMap: gradient, color: tinted ? def.tint : 0xffffff });
      mat.emissive = new THREE.Color(0x000000);
      model.traverse((o) => {
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.castShadow = true; m.receiveShadow = true; m.frustumCulled = false;
        if (weaponParts.has(m)) {
          // 무기 조각마다 자기 재질(텍스처 또는 단색)로 같은 툰 셰이딩 — 재질이 여럿인 무기는 메시가 여러 개다
          const wm = m.material as THREE.MeshStandardMaterial;
          const toon = new THREE.MeshToonMaterial({ map: wm.map ?? null, gradientMap: gradient, color: wm.map ? 0xffffff : wm.color });
          m.material = toon; this.owned.push(toon);
        } else m.material = mat;
      });
      const root = new THREE.Group();
      root.name = 'merc-' + def.role;
      const orient = new THREE.Group();
      orient.add(model);
      root.add(orient);
      // 정면 확인(모델마다): 대기 자세에서 발가락이 -Z면 반 바퀴
      let faceFlip = false;
      if (skinned) {
        const idle = actions.get('idle');
        if (idle) { idle.play(); mixer.update(0); }
        model.updateMatrixWorld(true);
        const bones = (skinned as THREE.SkinnedMesh).skeleton.bones;
        const toe = bones.find((b) => /LeftToeBase$/.test(b.name)), foot = bones.find((b) => /LeftFoot$/.test(b.name));
        if (toe && foot) faceFlip = toe.getWorldPosition(new THREE.Vector3()).z - foot.getWorldPosition(new THREE.Vector3()).z < 0;
        if (idle) idle.stop();
      }
      orient.rotation.y = faceFlip ? Math.PI : 0;
      // 손 무기: 생성 GLB가 있으면 그것(직업 모델·카엘 복제 공통), 없으면 카엘 복제에 절차 소품
      const kinds = ROLE_WEAPONS[def.role];
      const geos = await Promise.all(kinds.map((k) => weaponLoads.get(k)!));
      let glbWeapons = false;
      if (skinned && geos.every((g) => !!g)) {
        model.updateMatrixWorld(true);
        glbWeapons = kinds.every((k, i) => !!attachWeapon(model, k, geos[i]!, weaponMat));
      }
      // 무기 소품(카엘 복제, GLB 무기가 없을 때): 손 본 배율(리그 0.01)을 상쇄한다
      if (skinned && !hero && !glbWeapons) {
        const bones = (skinned as THREE.SkinnedMesh).skeleton.bones;
        model.updateMatrixWorld(true);
        const attach = (re: RegExp, geo: THREE.BufferGeometry | null, rx: number): void => {
          const bone = bones.find((b) => re.test(b.name));
          if (!bone || !geo) { geo?.dispose(); return; }
          const s = bone.getWorldScale(new THREE.Vector3()).x || 0.01;
          const pm = new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: gradient });
          const mesh = new THREE.Mesh(geo, pm);
          mesh.scale.setScalar(1 / s);
          mesh.rotation.x = rx;
          mesh.castShadow = true;
          bone.add(mesh);
          this.owned.push(geo, pm);
        };
        attach(/RightHand$/, prop(def.role), def.role === 'tank' ? Math.PI / 2 : 0);
        attach(/LeftHand$/, offProp(def.role), 0);
      }
      const roleClips = hero && heroRole
        ? { attack: clipName(hero, heroRole.attack), cast: clipName(hero, heroRole.cast) }
        : CLIPS[def.role];
      root.scale.setScalar(KAEL_SCALE * 0.94);
      const blob = new THREE.Mesh(blobGeo, blobMat);
      blob.position.y = 0.03 / (KAEL_SCALE * 0.94); blob.renderOrder = 2; blob.frustumCulled = false;
      root.add(blob);
      // 발밑 역할 고리·머리 위 이름표·HP 막대는 배율 없는 월드 좌표(별도 노드)
      const ringMat = new THREE.MeshBasicMaterial({ color: ROLE_RING[def.role], transparent: true, opacity: 0.7, depthWrite: false, fog: false, side: THREE.DoubleSide });
      ringMat.polygonOffset = true; ringMat.polygonOffsetFactor = -3; ringMat.polygonOffsetUnits = -3;
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.renderOrder = 3; ring.frustumCulled = false;
      const accent = '#' + ROLE_RING[def.role].toString(16).padStart(6, '0');
      const label = labelSprite(def.name + ' · ' + def.title, accent, 1.9, 'npc');
      const bgMat = new THREE.SpriteMaterial({ map: barTex, color: 0x101521, transparent: true, opacity: 0.85, depthTest: false, depthWrite: false, fog: false });
      const fillMat = new THREE.SpriteMaterial({ map: barTex, color: 0x5fd06a, depthTest: false, depthWrite: false, fog: false });
      const barBg = new THREE.Sprite(bgMat); barBg.scale.set(BAR_W + 0.06, BAR_H + 0.05, 1); barBg.renderOrder = 21;
      const barFill = new THREE.Sprite(fillMat); barFill.renderOrder = 22;
      this.owned.push(ringMat, label.material, label.material.map!, bgMat, fillMat);
      this.group.add(root, ring, label, barBg, barFill);
      this.units.push({ def, root, model, mixer, actions, mat, ring, label, barBg, barFill, cur: '', attack: roleClips.attack, cast: roleClips.cast, tinted, act: -1, actTick: 0, yaw: 0 });
    }
  }

  private play(u: Unit, name: string, once: boolean, fade: number, speed = 1): void {
    if (u.cur === name && !once) { const a = u.actions.get(name); if (a) a.timeScale = speed; return; }
    const next = u.actions.get(name) ?? u.actions.get('idle');
    if (!next) return;
    const prev = u.cur ? u.actions.get(u.cur) : undefined;
    next.reset();
    next.setLoop(once ? THREE.LoopOnce : THREE.LoopRepeat, Infinity);
    next.clampWhenFinished = once;
    next.timeScale = speed;
    next.play();
    if (prev && prev !== next) prev.crossFadeTo(next, fade, false);
    u.cur = name;
  }

  update(view: GameView, alpha: number, dt: number, clocks: GameClocks): void {
    const r = view.raid;
    const on = !!r && r.active && this.ready;
    this.group.visible = on;
    if (!on || !r) return;
    const wdt = clocks.worldDelta / 60;
    for (let i = 0; i < this.units.length; i++) {
      const u = this.units[i], m: MercView | undefined = r.mercs[i];
      // 넷 판: 사람이 차지한 용병 칸은 그리지 않는다(이름표·HP 막대·고리 포함)
      if (!m || m.visible === false) { u.root.visible = false; u.ring.visible = false; u.label.visible = false; u.barBg.visible = false; u.barFill.visible = false; continue; }
      u.root.visible = true; u.label.visible = true; u.barBg.visible = true;
      const x = m.prevX + (m.x - m.prevX) * alpha, z = m.prevZ + (m.z - m.prevZ) * alpha;
      u.root.position.set(x, 0, z);
      u.yaw += angleDelta(u.yaw, m.facing) * damp(16, dt);
      u.root.rotation.y = u.yaw;
      // 동작 전환: 같은 동작이 다시 시작되면(actTick이 줄면) 클립을 처음부터
      const restart = m.act !== u.act || m.actTick < u.actTick;
      u.act = m.act; u.actTick = m.actTick;
      if (m.act === ACT.down) { if (restart) this.play(u, 'death', true, 0.15); }
      else if (m.act === ACT.attack) { if (restart) this.play(u, u.attack, true, 0.1, 1.25); }
      else if (m.act === ACT.cast) { if (restart) this.play(u, u.cast, true, 0.12, 1.6); }
      else if (m.act === ACT.move) this.play(u, 'run', false, 0.15, m.evading ? 1.35 : 1.05);
      else this.play(u, 'idle', false, 0.2);
      u.mixer.update(wdt);
      // 피격 흰 발광·쓰러짐 회색
      const hurt = m.hurtTick < 6 && m.down === 0;
      u.mat.emissive.setHex(hurt ? 0xffffff : 0x000000);
      u.mat.emissiveIntensity = hurt ? 0.35 : 0;
      u.mat.color.setHex(m.down > 0 ? 0x6a6a72 : u.tinted ? u.def.tint : 0xffffff);
      // 고리·이름표·HP
      u.ring.position.set(x, 0.05, z);
      u.ring.visible = m.down === 0;
      u.label.position.set(x, HEAD_Y + 0.42, z);
      const ratio = m.maxHp > 0 ? Math.max(0, Math.min(1, m.hp / m.maxHp)) : 0;
      u.barBg.position.set(x, HEAD_Y, z);
      u.barFill.position.set(x, HEAD_Y, z);
      u.barFill.visible = ratio > 0.001;
      if (u.barFill.visible) {
        // 왼쪽 끝 고정: 화면 공간 기준점(center)을 막대 왼쪽 끝으로 옮긴다
        u.barFill.scale.set(BAR_W * ratio, BAR_H, 1);
        u.barFill.center.set(0.5 / ratio, 0.5);
        (u.barFill.material as THREE.SpriteMaterial).color.setHex(ratio > 0.5 ? 0x5fd06a : ratio > 0.25 ? 0xf2c14e : 0xe5533d);
      }
    }
  }

  /** 미리 컴파일용: 보이게 해 둔다 */
  prewarm(on: boolean): void { if (this.ready) this.group.visible = on; }

  dispose(): void {
    for (const u of this.units) { u.mixer.stopAllAction(); u.mat.dispose(); }
    for (const o of this.owned) o.dispose();
  }
}
